import type { PricingConfig } from './pricing/pricingEngine';

export type PaymentMethod = 'mollie' | 'bank_transfer' | 'on_site';
export type BookingStatus = 'pending_payment' | 'pending_validation' | 'confirmed' | 'cancelled' | 'expired' | 'failed';
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'expired' | 'refunded';
export type AttendanceStatus = 'expected' | 'reported_absent' | 'attended' | 'no_show';
export type RefundStatus = 'not_applicable' | 'pending' | 'refunded' | 'refused';
export type PartialRefundStatus = 'not_applicable' | 'pending_customer_iban' | 'pending_manual' | 'processed' | 'refused';
export type OutingPublicationStatus = 'draft' | 'published';
export type OutingPublicationAnnouncementStatus = 'not_started' | 'processing' | 'completed' | 'partial_failed' | 'failed';
export type TimestampLike = string | {
  seconds?: number;
  nanoseconds?: number;
  toDate?: () => Date;
  toMillis?: () => number;
};

export interface PartialRefund {
  status?: 'pending_customer_iban' | 'pending_manual' | 'refunded' | 'refused';
  amount?: number;
  ibanMasked?: string | null;
  accountHolderName?: string | null;
  communication?: string | null;
  refusedReason?: string | null;
}

export const ACTIVE_CAPACITY_BOOKING_STATUSES: BookingStatus[] = [
  'pending_payment',
  'pending_validation',
  'confirmed'
];

export const FINAL_BOOKING_STATUSES: BookingStatus[] = [
  'cancelled',
  'expired',
  'failed'
];

export function bookingOccupiesCapacity(status?: BookingStatus | null) {
  return !!status && ACTIVE_CAPACITY_BOOKING_STATUSES.includes(status);
}

export function isFinalBookingStatus(status?: BookingStatus | null) {
  return !!status && FINAL_BOOKING_STATUSES.includes(status);
}

export function resolveOutingPublicationStatus(outing?: Pick<Outing, 'publicationStatus'> | null): OutingPublicationStatus | null {
  if (outing?.publicationStatus === 'draft' || outing?.publicationStatus === 'published') {
    return outing.publicationStatus;
  }

  return null;
}

export function isOutingPublished(outing?: Pick<Outing, 'publicationStatus'> | null) {
  return outing?.publicationStatus === 'published';
}

export interface Outing {
  id: string;
  title: string;
  description: string;
  date: string; // ISO string
  duration: string;
  difficulty: 'Facile' | 'Moyen' | 'Difficile';
  pricingConfig?: PricingConfig;
  maxParticipants: number;
  currentParticipants: number;
  location: string;
  meetingPoint: string; // Google Maps URL
  requiredGear: string[];
  imageUrl: string;
  createdAt: TimestampLike;
  deleted?: boolean;
  paymentMethods: PaymentMethod[];
  bankTransferTemplate?: string;
  bankAccountOverride?: string;
  organizationId?: string;
  pmrAccessible?: boolean;
  strollerAccessible?: boolean;
  dogsAllowedOnLeash?: boolean;
  organizerCancellationMessage?: string;
  allowLateBookings?: boolean;
  lateBookingHoursBefore?: number;
  publicationStatus?: OutingPublicationStatus;
  publishedAt?: string | null;
  publishedByUid?: string | null;
  publicationAnnouncementStatus?: OutingPublicationAnnouncementStatus | null;
  publicationAnnouncementStartedAt?: string | null;
  publicationAnnouncementSentAt?: string | null;
  publicationAnnouncementSummary?: {
    emailTargeted: number;
    emailSucceeded: number;
    emailFailed: number;
    pushTargeted: number;
    pushSucceeded: number;
    pushFailed: number;
  } | null;
}

export interface Organization {
  id: string;
  paymentSettings?: {
    bankAccount?: string;
  };
}

export interface Booking {
  id: string;
  userId?: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  outingId: string;
  outingTitle: string;
  participantsCount: number;
  specialNeeds?: string;
  bookingStatus: BookingStatus;
  paymentMethod: PaymentMethod;
  paymentStatus: PaymentStatus;
  attendanceStatus: AttendanceStatus;
  refundStatus: RefundStatus;
  paymentId?: string;
  bankTransferCommunication?: string;
  bankTransferIban?: string;
  amount?: number;
  amountDueAfterPartialCancellation?: number;
  paymentValidatedAmount?: number;
  activeParticipantsCount?: number;
  cancelledParticipantsCount?: number;
  createdAt: TimestampLike;
  lastStatusUpdateAt?: TimestampLike;
  cancelledAt?: TimestampLike;
  cancelledBy?: string;
  cancellationReason?: string;
  cancellationActorRole?: string;
  organizerCancellationMessage?: string;
  absenceReportedAt?: TimestampLike;
  absenceReportedBy?: string;
  refundedAt?: TimestampLike;
  refundMethod?: 'manual' | 'provider';
  refundReason?: string;
  mollieStatus?: string;
  partialRefundStatus?: PartialRefundStatus;
  refundAmountRequested?: number;
  refundAmountProcessed?: number;
  partialRefund?: PartialRefund;

  // Champs legacy tolérés pendant la mise en place de la phase 1.
  status?: string;
  isPaid?: boolean;
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  role: 'admin' | 'user';
  fcmToken?: string;
}
